import config from '../config';

const SESSION_ID_KEY = 'henosis.analytics.session_id';
const ATTRIBUTION_KEY = 'henosis.analytics.attribution';
const INTERNAL_KEY = 'henosis.analytics.internal_marked';
const UTM_FIELDS = ['source', 'medium', 'campaign', 'content'];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EVENT_PROPERTIES = {
  landing_viewed: ['landing_variant'],
  landing_primary_cta_clicked: ['cta_label', 'page_section', 'destination_route', 'landing_variant'],
  landing_founder_onboarding_clicked: ['cta_label', 'page_section', 'landing_variant'],
  pricing_viewed: [],
  pricing_activation_clicked: ['cta_label', 'page_section', 'destination_route'],
  cli_install_copied: ['page_section'],
  remote_control_clicked: ['page_section', 'destination_route'],
  registration_started: [],
  checkout_started: ['checkout_type'],
};

// Only explicit signals opt out. Missing browser signals are not a consent state.
export const isAnalyticsAllowed = () => {
  try {
    if (typeof window === 'undefined') return false;
    return navigator.globalPrivacyControl !== true
      && ![navigator.doNotTrack, window.doNotTrack, navigator.msDoNotTrack]
        .some(value => value === '1' || value === 'yes');
  } catch (_) { return false; }
};

const randomUuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createAnalyticsId = randomUuid;

// Undefined is deliberately omitted by JSON.stringify in auth payloads. Do not
// fall back to device IDs or other identifying data when storage is blocked.
export const getAnalyticsSessionId = () => {
  if (!isAnalyticsAllowed()) return undefined;
  try {
    const stored = sessionStorage.getItem(SESSION_ID_KEY);
    if (stored && UUID_PATTERN.test(stored)) return stored.toLowerCase();
    const sessionId = randomUuid();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    return sessionId;
  } catch (_) { return undefined; }
};

const getAttribution = () => {
  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
    const source = existing ? JSON.parse(existing) : Object.fromEntries(
      UTM_FIELDS.map(field => [field, new URLSearchParams(window.location.search).get(`utm_${field}`)]),
    );
    const attribution = {};
    UTM_FIELDS.forEach(field => {
      const value = source?.[field];
      if (typeof value === 'string' && value.trim()) attribution[field] = value.trim().toLowerCase().slice(0, 80);
    });
    if (!existing) sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    return attribution;
  } catch (_) { return {}; }
};

const STATIC_ROUTES = new Set([
  '/', '/auth', '/login', '/register', '/verify-email', '/password-reset', '/reset-password',
  '/threads', '/chat', '/projects', '/pricing', '/activation', '/activate', '/credits',
  '/dashboard', '/settings', '/profile', '/agent', '/admin/analytics',
  '/founder-onboarding', '/terms', '/privacy',
]);

export const normalizeAnalyticsRoute = (value) => {
  const raw = String(value || '/').trim();
  let path;
  try { path = new URL(raw, 'https://analytics.henosis.invalid').pathname; }
  catch (_) { path = raw.split(/[?#]/, 1)[0]; }
  try { path = decodeURIComponent(path); } catch (_) { return '/other'; }
  path = `/${path.replace(/^\/+|\/+$/g, '').toLowerCase()}`;
  if (STATIC_ROUTES.has(path)) return path;
  if (/^\/shared\/[^/]+$/.test(path)) return '/shared/:id';
  if (/^\/chat\/[^/]+$/.test(path)) return '/chat/:id';
  if (/^\/projects\/[^/]+$/.test(path)) return '/projects/:id';
  return '/other';
};

export const sanitizeAnalyticsProperties = (eventName, properties) => {
  const allowed = new Set(EVENT_PROPERTIES[eventName] || []);
  const clean = {};
  Object.entries(properties || {}).forEach(([key, value]) => {
    if (!allowed.has(key) || !['string', 'number', 'boolean'].includes(typeof value)) return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;
    clean[key] = key === 'destination_route' ? normalizeAnalyticsRoute(value)
      : typeof value === 'string' ? value.trim().slice(0, 120) : value;
  });
  return clean;
};

export const trackAnalyticsEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'test' || !isAnalyticsAllowed()
    || !Object.prototype.hasOwnProperty.call(EVENT_PROPERTIES, eventName)) return;
  try {
    const sessionId = getAnalyticsSessionId();
    if (!sessionId) return;
    const payload = {
      event_id: randomUuid(),
      event_name: eventName,
      anonymous_session_id: sessionId,
      occurred_at: new Date().toISOString(),
      path: normalizeAnalyticsRoute(window.location.pathname),
      ...getAttribution(),
      properties: sanitizeAnalyticsProperties(eventName, properties),
    };
    const url = `${config.apiV2Url}/public/analytics/events`;
    const body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }))) return;
    } catch (_) { /* Try fetch if beacon is unavailable. */ }
    Promise.resolve(fetch(url, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body, keepalive: true,
    })).catch(() => {});
  } catch (_) { /* Storage, serialization and transport must never break auth/navigation. */ }
};

export const markCurrentSessionInternal = async () => {
  if (!isAnalyticsAllowed()) return;
  try {
    const sessionId = getAnalyticsSessionId();
    if (!sessionId || sessionStorage.getItem(INTERNAL_KEY) === '1') return;
    const response = await fetch(`${config.apiV2Url}/analytics/internal-session`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonymous_session_id: sessionId }),
    });
    if (response.ok) sessionStorage.setItem(INTERNAL_KEY, '1');
  } catch (_) {}
};
